import React, { useEffect, useMemo, useState } from "react";
import { ViewportElement } from "@foundation/viewport-element";

import { AD_TYPES } from "~components/shared/ad/utility";
import { GridZone, Zone } from "~components/shared/zones";
import { getModules } from "~components/shared/zones/utility";
import { modulesByIdFetched } from "~redux/features/modulesById-slice";
import useRedux from "~redux/hooks";
import type { CurationZone, HydratedCurationModule } from "~types/types";
import { isClient } from "~utils/isClient";

const LAZY_LOAD_BOTTOM_MARGIN = "600px";

export interface LazyZoneProps {
    className?: string;
    preview: boolean;
    zone: CurationZone;
    useGrid?: boolean;
}

interface ZoneModulesToFetch {
    moduleIds: string[];
    moduleTypes: string[];
    moduleVariations: string[];
}

const getZoneModulesToFetch = (zone: CurationZone): ZoneModulesToFetch => {
    const zoneModulesToFetch: ZoneModulesToFetch = {
        moduleIds: [],
        moduleTypes: [],
        moduleVariations: [],
    };

    const addModule = (
        modulesToFetch: ZoneModulesToFetch,
        {
            id,
            type,
            variation,
        }: { id: string | null; type: string | null; variation: string | null }
    ): void => {
        modulesToFetch.moduleIds.push(id || "");
        modulesToFetch.moduleVariations.push(variation || "default");
        modulesToFetch.moduleTypes.push(type || "");
    };

    return zone.columns.reduce(
        (accModulesToFetch, { modules: zoneModules }) => {
            zoneModules.forEach((module) => {
                // skip fetching programmatic ad modules since they are dynamically inserted
                if (module.adType === AD_TYPES.programmaticMobile) return;

                const { abbaTestVariants, ...restOfModule } =
                    module as HydratedCurationModule;

                addModule(accModulesToFetch, restOfModule);

                if (abbaTestVariants?.length) {
                    abbaTestVariants.forEach((variant) => {
                        if (variant) {
                            addModule(accModulesToFetch, variant);
                        }
                    });
                }
            });

            return accModulesToFetch;
        },
        zoneModulesToFetch
    );
};

export const LazyZone = ({
    className,
    preview,
    zone,
    useGrid = false,
}: LazyZoneProps) => {
    const { useAppSelector, useAppDispatch } = useRedux();
    const dispatch = useAppDispatch();
    const pageData = useAppSelector((state) => state.pageData);

    const [loaded, setLoaded] = useState(false);
    const [entered, setEntered] = useState(false);

    const fetchDataForModules = useMemo(async () => {
        if (!isClient()) return modulesByIdFetched({});

        const { locale, pageId, publishedState } = pageData;
        const zoneModules = getZoneModulesToFetch(zone);
        const { moduleIds, moduleVariations, moduleTypes } = zoneModules;

        try {
            const modulesById = await getModules(
                pageId,
                moduleIds.join(","),
                moduleVariations.join(","),
                moduleTypes.join(","),
                locale,
                preview,
                publishedState
            );

            return modulesByIdFetched(modulesById);
        } catch (error) {
            window?.newrelic?.noticeError(error, {
                ...pageData,
                zone: zone.name,
                zoneKind: zone.kind,
            });
        }

        return modulesByIdFetched({});
    }, [pageData, zone]);

    useEffect(() => {
        if (!entered) return;

        fetchDataForModules.then((fetchedModulesById) => {
            dispatch(fetchedModulesById);
            setLoaded(true);
        });
    }, [entered]);

    return (
        <ViewportElement
            rootMargin={`0px 0px ${LAZY_LOAD_BOTTOM_MARGIN} 0px`}
            onEnter={() => setEntered(true)}
            once
        >
            <div className="min-h-[20px]">
                {loaded && useGrid && (
                    <GridZone
                        zone={zone}
                        className={className}
                        preview={preview}
                    />
                )}
                {loaded && !useGrid && (
                    <Zone zone={zone} className={className} preview={preview} />
                )}
            </div>
        </ViewportElement>
    );
};
